/**
 * NewPathwayDialog (Sprint 4.3.7).
 *
 * Modal dialog triggered by the editor's "+ New Pathway" toolbar
 * button. The user enters a Name + Description; on submit the dialog
 * forwards to `onCreate` which calls the pathway store
 * `createPathway` action. The store wires the AuditEvent emission +
 * (live mode) FHIR write so this component stays presentation-only.
 *
 * Accessibility: the dialog is `role="dialog"` with
 * `aria-modal="true"` and an explicit labelled-by reference. The
 * Cancel button is the first focusable target (keyboard alternative
 * to the close button) and the Submit button is disabled until the
 * Name field is non-empty after trim.
 *
 * HC2 token-only. HC5 zero em / en dashes. HC6 a11y.
 *
 * Brand-Patch v2: zero TLP1-4 user-facing strings. The dialog never
 * names a clinical tier; the user types a free-form name.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { PathwayId } from '../../state/pathway-ids';

export interface NewPathwayDialogProps {
  open: boolean;
  pathwayIds: ReadonlyArray<PathwayId>;
  /**
   * The current tenant's existing pathway slugs that are already
   * authored. The dialog picks the first slug not already in use, so
   * the new pathway lands in an unused id slot. When all four slots are
   * taken, the dialog re-uses the active slug per the prototype's
   * fixed `PathwayId` enum (the seed is overwritten in place).
   */
  existingPathwayIds: ReadonlyArray<PathwayId>;
  onClose: () => void;
  onCreate: (args: {
    pathwayId: PathwayId;
    name: string;
    description: string;
  }) => void;
}

const NAME_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 280;

export function NewPathwayDialog({
  open,
  pathwayIds,
  existingPathwayIds,
  onClose,
  onCreate,
}: NewPathwayDialogProps) {
  const titleId = useId();
  const nameId = useId();
  const descriptionId = useId();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      // Defer focus until the dialog actually mounted in DOM.
      const t = setTimeout(() => cancelRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const isValid = trimmedName.length > 0;

  // Resolve the next available pathway-id slot. When all slots are
  // taken, fall back to the first id (overwrite-in-place is acceptable
  // for the prototype since the editor ships a fixed 4-id enum).
  const targetPathwayId =
    pathwayIds.find((id) => !existingPathwayIds.includes(id)) ?? pathwayIds[0];

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid) return;
    onCreate({
      pathwayId: targetPathwayId,
      name: trimmedName,
      description: trimmedDescription,
    });
  };

  return (
    <div
      data-testid="new-pathway-dialog-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center bg-text-primary/40 p-gutter-md"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="new-pathway-dialog"
        className="w-full max-w-md rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id={titleId}
          className="text-signal-base font-semibold text-text-primary"
        >
          Create a new pathway
        </h3>
        <p className="mt-1 text-signal-xs text-text-tertiary">
          Pathway slot: {targetPathwayId}
        </p>
        <form onSubmit={handleSubmit} className="mt-gutter-md flex flex-col gap-gutter-md">
          <div className="flex flex-col gap-1">
            <label
              htmlFor={nameId}
              className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
            >
              Name
            </label>
            <input
              id={nameId}
              data-testid="new-pathway-dialog-name"
              type="text"
              value={name}
              maxLength={NAME_MAX_LENGTH}
              required
              autoFocus
              onChange={(e) => setName(e.target.value)}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor={descriptionId}
              className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
            >
              Description
            </label>
            <textarea
              id={descriptionId}
              data-testid="new-pathway-dialog-description"
              value={description}
              rows={3}
              maxLength={DESCRIPTION_MAX_LENGTH}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            />
          </div>
          <div className="flex justify-end gap-gutter-sm">
            <button
              ref={cancelRef}
              type="button"
              data-testid="new-pathway-dialog-cancel"
              onClick={onClose}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-sm font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="new-pathway-dialog-submit"
              disabled={!isValid}
              className="rounded-signal-md bg-signal-primary-500 px-gutter-md py-1 text-signal-sm font-semibold text-text-inverse hover:bg-signal-primary-600 focus:outline-none focus:ring-2 focus:ring-signal-primary-500 disabled:opacity-60"
            >
              Create pathway
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
